import { CatalogType } from '../enums/catalog-type.enum';
import { Capabilities, type CapabilityAlias } from './capabilities';

/**
 * PD-17326 — file types per record type. A file belongs to the record it was
 * uploaded to, and each kind of record has its own tenant-maintained list of
 * file types in Settings → Catalog Values (one catalog per record type,
 * seeded with the defaults below). The chosen type's name is stored on
 * `_file._subtype`; `_file._type` holds the record type.
 *
 * This replaces the fixed customer list in code (PD-14342),
 * whose codes become the customer catalog's defaults and keep rendering
 * through {@link displayFileType}.
 */

/** The record types that take file uploads, as `_file._type` stores them. */
export type FileRecordType =
	| 'client'
	| 'opportunity'
	| 'job'
	| 'issue'
	| 'item'
	| 'license'
	| 'sourcing-request'
	| 'purchase-order-receipt'
	| 'contact';

export interface FileRecordTypeConfigModel {
	recordType: FileRecordType;
	catalogType: CatalogType;
	/** What the record is called in Settings ("Customer", "Opportunity"). */
	label: string;
	/** The types a tenant starts with, in order. */
	defaults: ReadonlyArray<string>;
	/**
	 * Bid documents arrive from someone: the upload asks when it was received
	 * and who sent it (an RFQ from the contractor, a competitor's bid).
	 */
	asksReceived: boolean;
	/** The plan capability the record type belongs to, when it is an add-on. */
	requiredCapability?: CapabilityAlias;
}

/** Bid and job documents (owner decision, PD-17317, 10/21/26). */
const BID_DOCUMENT_TYPES: ReadonlyArray<string> = [
	'RFQ',
	'Specifications',
	'Drawings',
	'Addendum',
	'Submittal',
	'Contract',
	'Correspondence',
	'Competitor bid',
];

export const FILE_RECORD_TYPES: ReadonlyArray<FileRecordTypeConfigModel> = [
	{
		recordType: 'client',
		catalogType: CatalogType.ClientFileType,
		label: 'Customer',
		// The fixed list it replaces, in the same order.
		defaults: ['Document', 'Contract', 'Agreement', 'License / Permit', 'Invoice', 'Tax Form', 'Insurance', 'Photo', 'Other'],
		asksReceived: false,
	},
	{
		recordType: 'opportunity',
		catalogType: CatalogType.OpportunityFileType,
		label: 'Opportunity',
		defaults: BID_DOCUMENT_TYPES,
		asksReceived: true,
	},
	{
		recordType: 'job',
		catalogType: CatalogType.JobFileType,
		label: 'Job',
		defaults: BID_DOCUMENT_TYPES,
		asksReceived: true,
		requiredCapability: Capabilities.JOBS,
	},
	{
		recordType: 'issue',
		catalogType: CatalogType.IssueFileType,
		label: 'Issue',
		defaults: ['Document', 'Photo', 'Correspondence', 'Other'],
		asksReceived: false,
	},
	{
		recordType: 'item',
		catalogType: CatalogType.ItemFileType,
		label: 'Item',
		defaults: ['Spec sheet', 'Safety data sheet', 'Photo', 'Document', 'Other'],
		asksReceived: false,
	},
	{
		recordType: 'license',
		catalogType: CatalogType.LicenseFileType,
		label: 'License',
		defaults: ['License', 'Inspection report', 'Correspondence', 'Other'],
		asksReceived: false,
	},
	{
		recordType: 'sourcing-request',
		catalogType: CatalogType.SourcingRequestFileType,
		label: 'Sourcing Request',
		defaults: ['Quote', 'Spec sheet', 'Photo', 'Document', 'Other'],
		asksReceived: false,
	},
	{
		recordType: 'purchase-order-receipt',
		catalogType: CatalogType.PurchaseOrderReceiptFileType,
		label: 'Purchase Order Receipt',
		defaults: ['Packing slip', 'Bill of lading', 'Invoice', 'Photo', 'Other'],
		asksReceived: false,
	},
	// PD-17589 — a person's own files (owner, 10/26/26). The first use is a
	// business card scanned at a trade show, saved on the person it created.
	{
		recordType: 'contact',
		catalogType: CatalogType.ContactFileType,
		label: 'Contact',
		defaults: ['Business card', 'Agreement', 'Headshot', 'Other'],
		asksReceived: false,
	},
];

/** The file-type settings of a record type. */
export function fileRecordTypeConfig(recordType: string): FileRecordTypeConfigModel | null {
	return FILE_RECORD_TYPES.find((config) => config.recordType === recordType) ?? null;
}

/** The record type a file-type catalog belongs to, or null for any other catalog. */
export function fileRecordTypeOfCatalog(catalogType: CatalogType): FileRecordTypeConfigModel | null {
	return FILE_RECORD_TYPES.find((config) => config.catalogType === catalogType) ?? null;
}

/**
 * Codes older uploads stored, and the label each shows as. New uploads store
 * the chosen type's name instead.
 *
 * - The fixed customer list in this app (PD-14342).
 * - The legacy CRM's customer file types (supplymover
 *   `ClientFileSubtype`), numbered. Where a seeded customer type means the
 *   same thing the code reads as that type, so it counts and filters with it.
 */
const LEGACY_FILE_TYPE_LABELS: Readonly<Record<string, string>> = {
	document: 'Document',
	contract: 'Contract',
	agreement: 'Agreement',
	license: 'License / Permit',
	invoice: 'Invoice',
	tax_form: 'Tax Form',
	insurance: 'Insurance',
	photo: 'Photo',
	other: 'Other',
	'1': 'Document',
	'2': 'Customer Application',
	// 3 and 4 (state board and DEA licenses) are not in the legacy CRM's own
	// list; another app adds them through its file-type hook. South Pointe holds
	// about 88,000 files with them (production, 10/22/26).
	'3': 'State Board License',
	'4': 'DEA License',
	'5': 'Insurance',
	'6': 'Voided Check',
	'7': 'Payment Authorization',
	'8': 'Quote',
	'9': 'Invoice',
	// Also added through that hook (South Pointe, Trustin).
	SOM_QUESTIONNAIRE: 'SOM Questionnaire',
};

/**
 * How a stored `_file._subtype` reads: a legacy code as its old label, a
 * catalog name as itself, nothing as null.
 */
export function displayFileType(subtype: string | null | undefined): string | null {
	const value = subtype?.trim() ?? '';

	if (value === '') {
		return null;
	}

	return LEGACY_FILE_TYPE_LABELS[value] ?? value;
}

/**
 * How a file's type reads on a record's Files tab. A file uploaded to the
 * record (or to one of its kind) shows its type; a file another part of Workd
 * made and linked here (a quote PDF on a customer: `_type` "quote") carries
 * that part's own data in `_subtype`, so it shows where it came from instead.
 */
export function displayFileTypeFor(
	file: { type: string | null | undefined; subtype: string | null | undefined },
	recordType: FileRecordType,
): string | null {
	const source = file.type?.trim() ?? '';

	if (source === '' || source === recordType) {
		return displayFileType(file.subtype);
	}

	const words = source.replace(/[-_]+/g, ' ');

	return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The stored values that read as `typeName`, for filtering by type: the name
 * itself and the legacy code that shows as it.
 */
export function fileTypeStoredValues(typeName: string): Array<string> {
	const legacy = Object.entries(LEGACY_FILE_TYPE_LABELS)
		.filter(([, label]) => label.toLowerCase() === typeName.trim().toLowerCase())
		.map(([code]) => code);

	return [typeName.trim(), ...legacy];
}
